import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AuthLayout, AuthField, SubmitButton } from "@/components/auth/AuthLayout";
import { PasswordRules, passwordOk } from "@/components/auth/PasswordRules";
import { useCaptcha, CaptchaField } from "@/components/auth/Captcha";
import { IdField, GAMLS_RULE, gamlsInvalid } from "@/components/member/IdField";
import { ProfileFields, EMPTY_PROFILE, profileError, FormSection, useMemberForm, stepNo } from "@/components/member/ProfileFields";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PasswordInput, ForgotDialog } from "@/pages/Login";
import { useApp } from "@/context/AppContext";
import { homeFor } from "@/lib/access";
import { api, errMsg } from "@/lib/api";
import { NoticeStep, useSignupNotice } from "@/components/notice/NoticeStep";
import { deviceFootprint } from "@/lib/device";
import { LoginNotice } from "@/components/auth/LoginNoticeBox";

const useDeviceBlocked = () => {
  const [blocked, setBlocked] = useState(null);
  useEffect(() => {
    deviceFootprint().then((d) => api.post("/devices/check", d)).then((r) => setBlocked(r.data.blocked)).catch(() => setBlocked(false));
  }, []);
  return blocked;
};

const DeviceBlocked = () => (
  <div className="text-center py-8 max-w-md mx-auto" data-testid="signup-device-blocked">
    <ShieldAlert className="w-10 h-10 text-[#E60000] mx-auto" />
    <h2 className="font-heading text-xl font-extrabold mt-3">No more accounts on this device</h2>
    <p className="text-sm text-gray-600 mt-2">This device has already been used to create 2 member accounts. You can still sign in to an existing account. If you need help, please contact the GAMLS secretariat.</p>
  </div>
);

const BLURB = "Create your member account once, then register for GAMLS events, pay and keep your receipts in one place.";
const EMPTY = { ...EMPTY_PROFILE, email: "", pin: "", gamls_id: "", staff_id: "", no_gamls_id: false, no_staff_id: false, no_pin: false, password: "", confirm: "", verify_channel: "email" };

const SignIn = ({ onDone, onForgot }) => {
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const captcha = useCaptcha("math");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onDone((await api.post("/auth/signin", { ...f, ...captcha.fields })).data); } catch (err) { toast.error(errMsg(err)); captcha.reload(); setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="member-login-form">
      <AuthField label="Email or username"><Input value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} autoComplete="username" className="h-11" required data-testid="member-login-email-input" /></AuthField>
      <AuthField label="Password"><PasswordInput value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testId="member-login-password-input" /></AuthField>
      <CaptchaField c={captcha} testId="member-login-captcha" />
      <div className="flex justify-end -mt-2"><button type="button" onClick={onForgot} className="text-sm font-semibold text-[#E60000] hover:underline" data-testid="member-forgot-link">Forgot password?</button></div>
      <SubmitButton busy={busy} testId="member-login-submit">{busy ? "Signing in…" : <>Sign in <ArrowRight className="w-4 h-4" /></>}</SubmitButton>
    </form>
  );
};

const DRAFT_KEY = "gamls_signup_draft";
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; } };

const SignUp = ({ onDone }) => {
  const [f, setF] = useState(() => ({ ...EMPTY, ...(loadDraft() || {}), password: "", confirm: "" }));
  const [restored, setRestored] = useState(() => !!loadDraft());
  useEffect(() => {
    const t = setTimeout(() => {
      const { password, confirm, ...rest } = f;
      if (JSON.stringify({ ...EMPTY, ...rest, password: "", confirm: "" }) === JSON.stringify({ ...EMPTY })) localStorage.removeItem(DRAFT_KEY);
      else localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
    }, 400);
    return () => clearTimeout(t);
  }, [f]);
  const clearDraft = () => { localStorage.removeItem(DRAFT_KEY); setF(EMPTY); setRestored(false); setPage(1); toast.success("Saved details cleared"); };
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const captcha = useCaptcha("math");
  const form = useMemberForm();
  const txt = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const detailsError = () => {
    const pErr = profileError(f, form);
    if (pErr) return pErr;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return "Enter a valid email address";
    if (!f.no_pin && !f.pin.trim()) return "Enter your AHPC PIN or tick 'I don't have an AHPC PIN'";
    if (!f.gamls_id.trim() && !f.no_gamls_id) return "Enter your GAMLS ID or tick 'I don't have a GAMLS ID'";
    if (!f.no_gamls_id && gamlsInvalid(f.gamls_id)) return `GAMLS ID must be ${GAMLS_RULE.text}`;
    if (!f.staff_id.trim() && !f.no_staff_id) return "Enter your Staff ID or tick 'I don't have a Staff ID'";
    return "";
  };
  const next = () => {
    const err = detailsError();
    if (err) return toast.error(err);
    setPage(2);
    captcha.reload();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (page === 1) return next();
    const dErr = detailsError();
    if (dErr) { setPage(1); return toast.error(dErr); }
    if (!passwordOk(f.password)) return toast.error("Your password does not meet the rules");
    if (f.password !== f.confirm) return toast.error("Passwords do not match");
    if (!captcha.answer.trim()) return toast.error("Please answer the security check");
    setBusy(true);
    try {
      const { confirm, ...body } = f;
      const res = (await api.post("/members/signup", { ...body, ...captcha.fields, ...(await deviceFootprint()) })).data;
      localStorage.removeItem(DRAFT_KEY);
      onDone(res);
    } catch (err) { toast.error(errMsg(err)); captcha.reload(); setBusy(false); }
  };
  if (form === null) return <div className="text-gray-500 py-10 text-center" data-testid="signup-form-loading">Loading form…</div>;
  if (!form) return <div className="text-[#E60000] py-10 text-center" data-testid="signup-form-error">Could not load the form. Please refresh the page.</div>;
  return (
    <form onSubmit={submit} noValidate data-testid="member-signup-form">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#E60000] mb-2" data-testid="signup-page-indicator">Page {page} of 2 · {page === 1 ? "Your details" : "Account security"}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mb-4" data-testid="signup-draft-note">
        <span>{restored ? "We kept the details you typed earlier on this device." : "Your details are saved on this device as you type (not your password)."}</span>
        <button type="button" onClick={clearDraft} className="font-semibold text-gray-700 hover:text-[#E60000] underline" data-testid="signup-clear-draft">Clear saved details</button>
      </div>
      {page === 1 ? <>
      <ProfileFields form={form} p={f} groupIds set={(p) => setF((s) => ({ ...s, ...p }))} slots={{
        pin: (label) => <AuthField label={label}><IdField label="AHPC PIN" value={f.pin} onChange={(v) => setF((s) => ({ ...s, pin: v }))} none={f.no_pin}
          setNone={(v) => setF((s) => ({ ...s, no_pin: v }))} noneLabel="I don't have an AHPC PIN" testId="signup-pin" /></AuthField>,
        email: (label) => <AuthField label={label}><Input type="email" value={f.email} onChange={txt("email")} className="h-11" data-testid="signup-email-input" /></AuthField>,
        gamls_id: (label) => <AuthField label={label}><IdField label="GAMLS ID" value={f.gamls_id} onChange={(v) => setF((s) => ({ ...s, gamls_id: v }))} none={f.no_gamls_id}
          setNone={(v) => setF((s) => ({ ...s, no_gamls_id: v }))} noneLabel="I don't have a GAMLS ID" testId="profile-gamls-id" rule={GAMLS_RULE} /></AuthField>,
        staff_id: (label) => <AuthField label={label}><IdField label="Staff ID" value={f.staff_id} onChange={(v) => setF((s) => ({ ...s, staff_id: v }))} none={f.no_staff_id}
          setNone={(v) => setF((s) => ({ ...s, no_staff_id: v }))} noneLabel="I don't have a Staff ID" testId="profile-staff-id" /></AuthField>,
      }} />
      <div className="flex justify-end pt-6 border-t border-[#E5E7EB]">
        <div className="sm:w-64 w-full"><SubmitButton testId="signup-next">Next: account security <ArrowRight className="w-4 h-4" /></SubmitButton></div>
      </div>
      </> : <>
      <p className="text-sm text-gray-600 mb-4" data-testid="signup-page2-note">Your details are saved on this page. Choose a password, answer the quick security check and create your account.</p>
      <FormSection step={stepNo(form.sections.length)} title="Account security" testId="profile-section-security">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <AuthField label="Password *"><PasswordInput value={f.password} onChange={txt("password")} autoComplete="new-password" testId="signup-password-input" /></AuthField>
          <AuthField label="Confirm password *"><PasswordInput value={f.confirm} onChange={txt("confirm")} autoComplete="new-password" testId="signup-confirm-input" /></AuthField>
        </div>
        <PasswordRules value={f.password} />
        <div className="max-w-md mt-4"><CaptchaField c={captcha} testId="signup-captcha" /></div>
      </FormSection>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-6 border-t border-[#E5E7EB]">
        <div className="sm:mr-auto" data-testid="signup-channel">
          <div className="text-xs font-semibold text-gray-700 mb-1">How should we send your 6-digit confirmation code?</div>
          <div className="flex gap-2">
            {[["email", "Email"], ["sms", "SMS to my phone"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setF((s) => ({ ...s, verify_channel: v }))} aria-pressed={f.verify_channel === v}
                className={`h-9 px-4 rounded-full border text-sm font-semibold transition-colors ${f.verify_channel === v ? "bg-[#111] text-white border-[#111]" : "bg-white text-gray-700 border-gray-300 hover:border-[#E60000]"}`} data-testid={`signup-channel-${v}`}>{l}</button>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">{f.verify_channel === "sms" ? "The code goes to your contact number. SMS can be used up to 2 times; email any time." : "The code goes to your email address."}</p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:w-auto">
          <button type="button" onClick={() => setPage(1)} className="h-11 px-5 rounded-md border border-gray-300 text-sm font-semibold hover:border-[#E60000] transition-colors" data-testid="signup-back">Back to my details</button>
          <div className="sm:w-64"><SubmitButton busy={busy} testId="signup-submit">{busy ? "Creating account…" : "Create account"}</SubmitButton></div>
        </div>
      </div>
      </>}
    </form>
  );
};

export default function MemberAuth() {
  const { auth, signIn } = useApp();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "signup" ? "signup" : "signin");
  const [forgot, setForgot] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const notice = useSignupNotice();
  const blocked = useDeviceBlocked();
  const nav = useNavigate();
  const next = params.get("next") || "/my";
  const dest = (m) => (!m.email_verified ? `/member/verify?next=${encodeURIComponent(next)}` : !m.profile_complete ? `/my/profile?next=${encodeURIComponent(next)}` : next);
  const staffNext = next.startsWith("/resources") ? next : null;
  if (auth?.kind === "staff") return <Navigate to={auth.must_change_password ? "/change-password" : staffNext || homeFor(auth)} replace />;
  if (auth?.kind === "member") return <Navigate to={auth.must_change_password ? "/change-password" : dest(auth)} replace />;
  const done = (m) => {
    signIn(m);
    if (m.kind === "staff") {
      toast.success(`Welcome, ${m.name}`);
      return nav(m.must_change_password ? "/change-password" : staffNext || homeFor(m));
    }
    if (m.must_change_password) {
      toast.success("Signed in. Please set a new password.");
      return nav("/change-password");
    }
    toast.success(m.email_verified ? `Welcome back, ${m.first_name || m.name}` : "Account created. Check your email for the code.");
    nav(dest(m));
  };
  const tabCls = (t) => `flex-1 h-10 rounded-md text-sm font-semibold transition-colors ${tab === t ? "bg-white shadow-sm text-[#111]" : "text-gray-500 hover:text-[#111]"}`;
  const pick = (t) => { setTab(t); window.scrollTo(0, 0); };
  const tabs = (
    <div className="flex gap-1 p-1 bg-[#F3F4F6] rounded-lg mt-6 mb-6 max-w-md">
      <button type="button" onClick={() => pick("signin")} className={tabCls("signin")} data-testid="member-tab-signin">Login</button>
      <button type="button" onClick={() => pick("signup")} className={tabCls("signup")} data-testid="member-tab-signup">Create account</button>
    </div>
  );
  if (tab === "signup") {
    return (
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="member-signup-page">
        <PublicHeader />
        <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E60000]">Member account</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Create your account</h1>
          <p className="text-sm text-gray-500 mt-1">{blocked ? "Account creation is not available on this device." : notice && !accepted ? "Please read this notice before you fill in your details." : "Fill in your details once. They are used for your registrations, badge, receipts and certificates. Fields marked * are required."}</p>
          {tabs}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 sm:p-10">
            {notice === null || blocked === null ? <div className="text-gray-500 py-10 text-center">Loading…</div>
              : blocked ? <DeviceBlocked />
              : notice && !accepted ? <NoticeStep html={notice} onAccept={() => { setAccepted(true); window.scrollTo(0, 0); }} />
              : <SignUp onDone={done} />}
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }
  return (
    <AuthLayout blurb={BLURB}>
      <h2 className="font-heading text-3xl font-extrabold tracking-tight">Login</h2>
      <LoginNotice />
      {tabs}
      <SignIn onDone={done} onForgot={() => setForgot(true)} />
      <ForgotDialog open={forgot} onClose={() => setForgot(false)} account="any" />
    </AuthLayout>
  );
}
